import { FC, ReactNode, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import { EntertainerBadge, useBadgesFeedQuery } from '@queries/CritiqueQueries';
import {
  useCurrentEntertainerBadgesQuery,
  useCurrentEntertainerQuery,
} from '@queries/EntertainerQueries';
import { Notification, NotificationType, useNotificationsQuery } from '@queries/UserQueries';

import { useSendEntertainerBadge } from '@mutations/EntertainerMutations';
import {
  useMarkNotificationAsReadMutation,
  useMarkNotificationsAsSeenMutation,
} from '@mutations/UserMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCanInstallIOSApp } from '@hooks/useCanInstallIOSApp';
import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';
import { useDevice } from '@hooks/useDevice';
import { usePWA } from '@hooks/usePWA';

import { NavigateFunction, useNavigate, useRouter } from '@router/index';

import dayjs from '@utilities/dayjs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { List, ListItemProps } from '@components/layout/List/List';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { SendBadgePopup, UnlockBadgePopup } from './UnlockBadgePopup';

const EntertainerBadgeRow: FC<{ badge: EntertainerBadge }> = ({ badge }) => {
  const { track } = useAnalytics();
  const [isExpanded, setIsExpanded] = useState(false);
  const [didSend, setDidSend] = useState(false);
  const { isSubmitting, sendEntertainerBadgeAsync } = useSendEntertainerBadge();
  const badgeSize = 32;

  const handleSendBadge = async () => {
    track('Send feed badge', {
      badgeId: badge._id,
      entertainerMediaId: badge.entertainerMedia ? badge.entertainerMediaId : undefined,
      reason: badge.reason,
    });

    await sendEntertainerBadgeAsync({
      badgeId: badge._id,
      entertainerId: badge.to.entertainerId,
      entertainerMediaId: badge.entertainerMedia ? badge.entertainerMediaId : undefined,
      reason: badge.reason,
    });

    setDidSend(true);
  };

  return (
    <div
      className="d-flex align-items-center p-2 w-100"
      style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
    >
      <div className="w-100">
        <div className="text-truncate w-100">
          <div className="d-flex gap-2 align-items-center justify-content-between mb-1">
            <div>
              <strong className="text-truncate">{badge?.to?.title}</strong>
            </div>
            <div style={{ fontSize: '.7rem', opacity: 0.8 }}>{dayjs(badge.sentAt).fromNow()}</div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div className="px-2">
              <img
                src={badge.imageUrl}
                style={{ height: badgeSize, width: badgeSize, objectFit: 'contain' }}
              />
            </div>
            <div className="text-truncate">
              <div style={{ opacity: 0.8 }}>
                <div
                  style={{ fontSize: '.6rem', textTransform: 'uppercase', letterSpacing: '.1rem' }}
                >
                  {badge.title}
                </div>
                {!badge.entertainerMediaId && (
                  <div style={{ fontSize: '.8rem' }}>
                    <strong>
                      {badge.reason}
                      {badge.eventTitle ? ` @ ${badge.eventTitle}` : undefined}
                    </strong>
                  </div>
                )}
                {badge.entertainerMediaId && badge.eventTitle && (
                  <div className="text-truncate" style={{ fontSize: '.8rem' }}>
                    <strong>{badge.eventTitle}</strong>
                  </div>
                )}
              </div>
            </div>
            <div className="ml-auto">
              <IconButton color="neutral" variant="flat" onClick={() => setIsExpanded(!isExpanded)}>
                <FontAwesomeIcon name={isExpanded ? 'chevron-up' : 'chevron-down'} />
              </IconButton>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="mt-3">
            <div>
              <Button
                disabled={isSubmitting || didSend}
                roundness="rounded"
                variant="smooth"
                color="neutral"
                onClick={handleSendBadge}
              >
                {didSend ? 'Sent!' : 'Yasss, send from me too'}
              </Button>
            </div>
            {badge.entertainerMedia && (
              <div className="mt-2">
                <AsyncImage
                  src={badge.entertainerMedia.imageUrl}
                  style={{ maxWidth: '100%', borderRadius: 8 }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NOTIFICATION_ACTION_RECORD: Record<
  NotificationType,
  {
    label: string;
    to: (notification: Notification) => string;
  }
> = {
  suggestedEvents: { label: 'View events', to: () => '/events/suggested' },
};

const NotificationIcon: FC<{
  isUnread: boolean;
  avatarLetter: string;
}> = ({ isUnread, avatarLetter }) => {
  return (
    <div className="notification-media">
      {isUnread && (
        <div className="notification-unread-icon">
          <FontAwesomeIcon name="circle" />
        </div>
      )}
      <LetterAvatar letter={avatarLetter} />
    </div>
  );
};

const TAB_OPTION_RECORD = {
  feed: 'Feed',
  received: 'Earned',
  sent: 'Given',
};
type TabOption = keyof typeof TAB_OPTION_RECORD;

const TAB_OPTIONS = Object.entries(TAB_OPTION_RECORD).map(([value, label]) => ({ value, label }));

export const EntertainerBadgesScreen: FC<{}> = () => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { entertainerIsReady, entertainer } = useCurrentEntertainerQuery();
  const { currentCity } = useCurrentUserCity();
  const { badgeFeed, badgeFeedIsReady } = useBadgesFeedQuery({ cityId: currentCity?._id });
  const { entertainerBadgesAreReady, receivedBadges, sentBadges } =
    useCurrentEntertainerBadgesQuery(entertainer?._id);
  const [currentTab, setCurrentTab] = useState<TabOption>('feed');
  const [lockedBadgeId, setLockedBadgeId] = useState<string>();
  const [sendBadgePopupIsVisible, setSendBadgePopupIsVisible] = useState(false);

  const [postPage, setPostPage] = useState(1);
  const filteredBadgeFeed = useMemo(() => {
    return badgeFeed?.slice(0, postPage * 20);
  }, [badgeFeed, postPage]);

  const hasReachedEnd = useMemo(() => {
    if (currentTab === 'feed') {
      return badgeFeedIsReady && badgeFeed?.length === filteredBadgeFeed?.length;
    }

    return true;
  }, [currentTab, badgeFeedIsReady, badgeFeed, filteredBadgeFeed]);

  const handleLoadMore = async () => {
    if (currentTab === 'feed') {
      setPostPage((prev) => prev + 1);
    }
  };

  const handleShareBadge = () => {
    navigate('/engagement/support', {
      state: {
        forceBack: true,
      },
    });
  };

  const handleUnlockBadge = (badge: EntertainerBadge) => {
    if (badge.isLocked) {
      track('Click unlock badge');
      setLockedBadgeId(badge._id);
    }
  };

  return (
    <Screen
      name="Badges Screen"
      headerProps={{
        title: '',
        back: {
          to: '/manage/entertainer',
        },
      }}
      hideToolbar
    >
      <Screen.Content
        infiniteScroll={{
          hasReachedEnd,
          onLoadMore: handleLoadMore,
        }}
      >
        <TitleToolbar text={<span className="fs-3">Badges & Endorsements</span>} size="md" />
        <ButtonFilter
          options={TAB_OPTIONS.map((option) => ({
            ...option,
            // badgeCount: option.value === 'received' ? receivedBadges?.length : 0,
          }))}
          onChange={(value) => {
            track(`Switch to ${value} badges`);
            setCurrentTab(value as TabOption);
          }}
          selected={currentTab}
        />
        {badgeFeedIsReady && currentTab === 'feed' && (
          <>
            <TitleToolbar text={`Recently Shared Badges in ${currentCity?.name}`} size="sm" />

            <div className="d-flex flex-column gap-3">
              {filteredBadgeFeed?.map((badge) => (
                <EntertainerBadgeRow key={badge._id} badge={badge} />
              ))}
            </div>
          </>
        )}
        {entertainerIsReady && entertainerBadgesAreReady ? (
          <>
            {currentTab === 'received' && (
              <>
                <TitleToolbar
                  className="mb-2"
                  text={`${receivedBadges?.length} ${pluralize('Achievement', receivedBadges?.length)} Earned`}
                  size="sm"
                  action={
                    <Button
                      size="sm"
                      className="px-3 py-2"
                      roundness="rounded"
                      variant="flat"
                      color="primary"
                      onClick={handleShareBadge}
                    >
                      Share Badges
                    </Button>
                  }
                />
                {receivedBadges?.length ? (
                  <List
                    className="notification-list mt-0"
                    listItems={receivedBadges.map((badge) => ({
                      startIcon: (
                        <img
                          src={badge.imageUrl}
                          style={{ height: 42, width: 42, marginTop: -2, marginBottom: -8 }}
                        />
                      ),
                      title: badge.isLocked
                        ? 'Locked'
                        : ((
                            <span className="text-truncate">{badge?.from?.displayName}</span>
                          ) as any),
                      subTitle: badge.isLocked
                        ? undefined
                        : ((
                            <>
                              <div>{`${badge.title} for ${badge.reason}`}</div>
                              {badge.eventTitle && (
                                <div>
                                  <strong>{badge.eventTitle}</strong>
                                </div>
                              )}
                            </>
                          ) as any),
                      endIcon: (
                        <div>
                          {badge.isLocked ? (
                            <Button
                              size="sm"
                              className="px-3 py-2"
                              roundness="rounded"
                              variant="smooth"
                              color="neutral"
                              onClick={() => handleUnlockBadge(badge)}
                            >
                              Unlock
                            </Button>
                          ) : (
                            <>
                              {badge?.from?.entertainerId && (
                                <SendEntertainerBadgeButton
                                  entertainerId={badge?.from?.entertainerId}
                                  text="Share back"
                                  buttonProps={{
                                    size: 'sm',
                                    startIcon: undefined,
                                    className: 'px-3 py-2',
                                    roundness: 'rounded',
                                    variant: 'smooth',
                                    color: 'neutral',
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <EmptyState
                    title="No badges yet"
                    text="You haven't received any badges yet, but you can still send some to your fellow entertainers."
                    button={{
                      text: 'Share a badge',
                      onClick: handleShareBadge,
                    }}
                  />
                )}
              </>
            )}
            {currentTab === 'sent' && (
              <>
                <TitleToolbar
                  className="mb-2"
                  text={`${sentBadges?.length} ${pluralize('Badge', sentBadges?.length)} Given`}
                  size="sm"
                />
                {sentBadges?.length ? (
                  <List
                    className="notification-list mt-0"
                    listItems={sentBadges.map((badge) => ({
                      startIcon: (
                        <img
                          src={badge.imageUrl}
                          style={{ height: 42, width: 42, marginTop: -2, marginBottom: -8 }}
                        />
                      ),
                      title: (<span className="text-truncate">{badge?.to?.title}</span>) as any,
                      subTitle: (
                        <>
                          <div>{`${badge.title} for ${badge.reason}`}</div>
                          {badge.eventTitle && (
                            <div>
                              <strong>{badge.eventTitle}</strong>
                            </div>
                          )}
                        </>
                      ) as any,
                      // endIcon: (
                      //   <SendEntertainerBadgeButton
                      //     entertainerId={badge?.to?.entertainerId}
                      //     lockedBadgeId={lockedBadgeId}
                      //     text="Send Another"
                      //     buttonProps={{
                      //       size: 'sm',
                      //       startIcon: undefined,
                      //       className: 'px-3 py-2',
                      //       roundness: 'rounded',
                      //       variant: 'smooth',
                      //       color: 'neutral',
                      //     }}
                      //   />
                      // ),
                    }))}
                  />
                ) : (
                  <EmptyState
                    title="No badges yet"
                    text="You haven't sent any badges yet. Share a badge and spread the love!"
                    button={{
                      text: 'Share a badge',
                      onClick: handleShareBadge,
                    }}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <div className="grid gap-2">
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </div>
        )}
      </Screen.Content>
      <SendBadgePopup
        isVisible={sendBadgePopupIsVisible}
        onClose={() => {
          setSendBadgePopupIsVisible(false);
        }}
      />
      <UnlockBadgePopup
        isVisible={!!lockedBadgeId}
        lockedBadgeId={lockedBadgeId}
        onClose={() => {
          setLockedBadgeId(undefined);
        }}
      />
    </Screen>
  );
};
